import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Trash2, Loader2 } from 'lucide-react';
import { sceneThumb } from '../../lib/sceneImages';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/fabrics/colors`;

/* Admin-only: set the swatch's default room or delete a bad scene */
const AdminThumbActions = ({ room, isDefault, colorId, onChanged, fold }) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const undo = async (binId) => {
    try {
      const { data } = await axios.post(`${API.replace('/colors', '')}/deleted-scenes/${binId}/restore`, {}, { headers });
      onChanged('restore', room, data);
      toast.success(`${room.label} scene restored`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
  };

  const run = async (e, action) => {
    e.stopPropagation();
    const what = fold ? `${fold.charAt(0).toUpperCase() + fold.slice(1)} fold "${room.label}"` : `"${room.label}"`;
    if (action === 'delete' && !window.confirm(`Delete the ${what} scene for this color? It stays in the Restore Bin for 7 days.`)) return;
    setBusy(true);
    try {
      const { data } = action === 'delete'
        ? await axios.delete(`${API}/${colorId}/rooms/${room.id}`, { headers, params: fold ? { fold } : {} })
        : await axios.put(`${API}/${colorId}/default-room`, { room_type: room.id }, { headers });
      onChanged(action, room);
      if (action === 'delete') {
        toast.success(`${room.label} scene deleted`, {
          duration: 10000,
          action: { label: 'Undo', onClick: () => undo(data.bin_id) },
        });
      } else toast.success(`${room.label} is now the default scene`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  if (busy) return <div className="absolute top-1 right-1 bg-white/90 rounded p-0.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /></div>;
  return (
    <div className="absolute top-1 right-1 flex gap-0.5">
      {!fold && <button type="button" onClick={(e) => run(e, 'default')} disabled={isDefault} title={isDefault ? 'Default scene' : 'Set as default for this color'}
        className="bg-white/90 hover:bg-white rounded p-0.5 shadow-sm" data-testid={`room-set-default-${room.id}`}>
        <Star className={`w-3.5 h-3.5 ${isDefault ? 'fill-amber-400 text-amber-500' : 'text-neutral-500'}`} />
      </button>}
      <button type="button" onClick={(e) => run(e, 'delete')} title="Delete this scene"
        className="bg-white/90 hover:bg-red-50 rounded p-0.5 shadow-sm" data-testid={`room-delete-${room.id}`}>
        <Trash2 className="w-3.5 h-3.5 text-red-600" />
      </button>
    </div>
  );
};

/* Scene Style Picker: switch between generated AI room styles */
export const RoomStylePicker = ({ rooms, activeIdx, defaultRoom, colorId, onPick, onChanged, fold = null }) => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  if (rooms.length < (isAdmin ? 1 : 2)) return null;
  return (
    <div className="mt-2" data-testid="room-style-picker">
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1.5">
        Room style{fold && ` · ${fold} fold`}{isAdmin && !fold && <span className="normal-case tracking-normal font-normal text-neutral-400"> · star = default for this color</span>}
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="room-switcher-gallery">
        {rooms.map((room, idx) => (
          <div
            key={room.id}
            role="button"
            tabIndex={0}
            onClick={() => onPick(idx)}
            className={`relative flex-shrink-0 w-24 cursor-pointer rounded-lg overflow-hidden border-2 transition-[border-color,opacity,transform] duration-200 hover:-translate-y-0.5 ${
              idx === activeIdx ? 'border-amber-500 ring-2 ring-amber-200' : 'border-neutral-200 hover:border-neutral-400 opacity-80 hover:opacity-100'
            }`}
            data-testid={`room-thumb-${room.id}`}
          >
            <img src={sceneThumb(room.url)} alt={room.label} className="w-full h-14 object-cover" loading="lazy" />
            <p className={`text-[10px] text-center py-1 font-medium truncate px-1 ${idx === activeIdx ? 'bg-amber-50 text-amber-800' : 'bg-white text-neutral-600'}`}>{room.label}</p>
            {isAdmin && <AdminThumbActions room={room} isDefault={room.id === defaultRoom} colorId={colorId} onChanged={onChanged} fold={fold} />}
          </div>
        ))}
      </div>
    </div>
  );
};
